import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { Avatar, Box, IconButton, InputAdornment, Paper, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import IconifyIcon from 'components/base/IconifyIcon';
import DataGridPagination from 'components/pagination/DataGridPagination';
import StyledTextField from 'components/styled/StyledTextField';

const CustomerForDocument = () => {
  const [customer, setCustomer] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: '',
  });
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, search }));
    }, 600);
    return () => clearTimeout(timeOut);
  }, [search]);

  const ViewButton = ({ row }) => {
    const navigate = useNavigate();

    const handleView = () => {
      navigate('/documents', { state: { state: row, from: '/customer-documents' } });
    };

    return (
      <IconButton onClick={handleView}>
        <RemoveRedEyeOutlinedIcon />
      </IconButton>
    );
  };

  const columns = useMemo(
    () => [
      {
        field: 'view',
        headerName: 'View',
        filterable: false,
        sortable: false,
        minWidth: 80,
        renderCell: (params) => <ViewButton row={params.row} />,
      },
      {
        field: 'client',
        headerName: 'Client',
        minWidth: 240,
        filterable: false,
        sortable: false,
        flex: 1,
        renderCell: (params) => {
          const { firstName, lastName, mobile } = params.row;

          return (
            <Stack
              sx={{
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <Avatar
                alt={firstName.toUpperCase()}
                src={firstName}
                sx={{ width: 24, height: 24, bgcolor: '#977efdff' }}
              />
              <div>
                <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                  {firstName} {lastName}
                </Typography>
                <Link to="#!" variant="caption">
                  {mobile || ''}
                </Link>
              </div>
            </Stack>
          );
        },
      },
      {
        field: 'assignManager',
        headerName: 'Sales Manager',
        filterable: false,
        sortable: false,
        minWidth: 170,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {params.row.assignManager.firstName} {params.row.assignManager.lastName}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'assignSalesSurveyorId',
        headerName: 'Sales Surveyor',
        filterable: false,
        sortable: false,
        minWidth: 170,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {params.row.assignSalesSurveyorId.firstName}{' '}
                {params.row.assignSalesSurveyorId.lastName}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'location',
        headerName: 'Location',
        filterable: false,
        sortable: false,
        minWidth: 150,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {params.row.city.name}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'dateOfEnquiery',
        headerName: 'Date Of Enquiery',
        filterable: false,
        sortable: false,
        minWidth: 150,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {moment(params.row.createdAt).format('DD/MM/YYYY')}
              </Typography>
            </Box>
          );
        },
      },

      {
        field: 'requirement',
        headerName: 'Requirement',
        filterable: false,
        sortable: false,
        minWidth: 150,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {params.row.appliedCapacity} KW
              </Typography>
            </Box>
          );
        },
      },
      // {
      //   field: 'action',
      //   headerName: 'Action',
      //   filterable: false,
      //   sortable: false,
      //   align: 'right',
      //   width: 60,
      //   headerAlign: 'right',
      //   renderCell: () => <DashboardMenu />,
      // },
    ],
    [],
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      // /list_customers
      const response = await post('/users/list_customers_stages', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
        type: 'document',
      });
      if (response.status == 200) {
        setCustomer(response.data.data);
        setTotal(response.data.total);
      } else {
        enqueueSnackbar('Error while fetching customers!', {
          variant: 'error',
          autoHideDuration: 3000,
        });
        setCustomer([]);
        setTotal(0);
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error while fetching customers!', {
        variant: 'error',
        autoHideDuration: 3000,
      });
      setCustomer([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [paginationModel]);

  return (
    <>
      <Paper sx={{ flex: 1, p: { xs: 3, md: 5 } }}>
        <StyledTextField
          id="search-box"
          type="search"
          fullWidth
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search Customer"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            maxWidth: { sm: 200, md: 240 },
            flexGrow: { xs: 1, sm: 0 },
            mb: 3,
          }}
        />
        <Box sx={{ width: '100%' }}>
          <DataGrid
            loading={loading}
            rowHeight={72}
            rows={customer}
            columns={columns}
            getRowId={(row) => row._id}
            pagination
            paginationMode="server"
            rowCount={total}
            paginationModel={paginationModel} // { page: 0, pageSize: 10 }
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 20]}
            sx={{
              '& .margin': {
                pr: 3,
              },
              '& .MuiDataGrid-columnHeaders': {
                minWidth: '100%',
              },
            }}
            slots={{
              basePagination: (props) => <DataGridPagination showFullPagination {...props} />,
            }}
          />
        </Box>
      </Paper>
    </>
  );
};
export default CustomerForDocument;
